<template>
  <div class="ThinWall" :class="{ 'is-english': isEnglish }" ref="section">
    <div
      class="ThinWall-animation"
      :class="{ 'animation-triggered': imgVisible }"
    >
      <div class="frame-container">
        <!-- 反向顯示對應的 frame -->
        <img
          v-if="imgVisible && frames.length"
          :src="frames[frames.length - 1 - currentFrame]"
          alt="Thin Wall Frame"
          class="frame-img"
        />
      </div>
      <!-- 反向滑桿：v-model 綁 currentFrame 不變，但顯示反過來 -->
      <input
        v-if="imgVisible && frames.length"
        type="range"
        min="0"
        :max="frames.length - 1"
        v-model.number="currentFrame"
        class="frame-slider"
      />
    </div>
    <h1 class="ThinWall-title">
      <span class="gradient animate-line delay-1">
        <div v-html="$t('TskSelectNeedle.ThinWall1')"></div>
      </span>
      <span class="line-1 animate-line delay-2">
        <div v-html="$t('TskSelectNeedle.ThinWall2')"></div>
      </span>
    </h1>
  </div>
</template>

<script>
export default {
  data() {
    return {
      imgVisible: false,
      hasPlayed: false,
      isMobile: false,
      frames: [],
      currentFrame: 0,
    }
  },
  computed: {
    isEnglish() {
      return String(this.$i18n.locale || '').toLowerCase().startsWith('en')
    },
    frameFolder() {
      const publicBase = (process.env.BASE_URL || '/').replace(/\/$/, '')
      const locale = this.$i18n.locale
      const isEN = locale === 'en'
      const isZH = locale === 'zh-TW'
      const folderLang = isEN ? 'EN' : isZH ? '' : 'EN'

      if (isZH) {
        return `${publicBase}/mp4/TSN/ThinWall/${this.isMobile ? 'phone_new' : 'web_new'}`
      }

      if (this.isMobile) {
        return `${publicBase}/mp4/TSN/ThinWall/phone${folderLang ? '_' + folderLang : ''}`
      } else {
        return `${publicBase}/mp4/TSN/ThinWall/web${folderLang ? '_' + folderLang : ''}`
      }
    },
    frameCount() {
      if (this.$i18n.locale === 'zh-TW') {
        return this.isMobile ? 87 : 84
      }
      return this.isMobile ? 75 : 83
    }
  },
  mounted() {
    this.checkDevice()
    window.addEventListener("resize", this.checkDevice)
    window.addEventListener("orientationchange", this.checkDevice)

    this.generateFrames()

    this.$nextTick(() => {
      this.currentFrame = 0
      this.imgVisible = true
    })

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.66 && !this.hasPlayed) {
          this.hasPlayed = true
          const target = Math.floor((this.frames.length - 1) * 2 / 3)
          setTimeout(() => {
            this.animateSliderToTarget(target)
          }, 200)
          observer.disconnect()
        }
      },
      { threshold: [0, 0.33, 0.66, 1] }
    )
    observer.observe(this.$refs.section)
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkDevice)
    window.removeEventListener("orientationchange", this.checkDevice)
  },
  methods: {
    checkDevice() {
      const isPortrait = window.innerHeight > window.innerWidth
      const ua = navigator.userAgent
      const isiPad = /iPad/.test(ua) || (ua.includes("Macintosh") && 'ontouchend' in document)
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

      const wasMobile = this.isMobile
      this.isMobile = isPortrait && (window.innerWidth <= 820 || isiPad || isTouchDevice)
      if (this.isMobile !== wasMobile) this.generateFrames()
    },
    generateFrames() {
      this.frames = Array.from({ length: this.frameCount }, (_, i) =>
        `${this.frameFolder}/frame_${(i + 1).toString().padStart(3, "0")}.webp`
      );
      this.currentFrame = 0;

      // ✅ 預載入所有 frame 圖片
      this.frames.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    },
    animateSliderToTarget(target) {
      const duration = 700
      const start = this.currentFrame
      const end = target
      const startTime = performance.now()

      const animate = (now) => {
        const elapsed = now - startTime
        const progress = Math.min(elapsed / duration, 1)
        const eased = 1 - Math.pow(1 - progress, 3)
        this.currentFrame = Math.round(start + (end - start) * eased)

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          this.currentFrame = end
        }
      }
      requestAnimationFrame(animate)
    }
  },
  watch: {
    frameFolder() {
      this.generateFrames()
    },
    '$i18n.locale'() {
      this.generateFrames()
    }
  }
}
</script>

<style scoped>
@keyframes fadeUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
.ThinWall {
/* removed min-height */
/* removed flex */
/* removed justify-content */
/* removed align-items */
  position: relative;
  overflow: hidden;
  width: 100%;
  margin: 0;
  padding: 0;
  background: #000;
  box-sizing: border-box;
}

.ThinWall-animation {
  opacity: 0;
  transition: opacity 0.8s ease;
}
.ThinWall-animation.animation-triggered {
  opacity: 1;
}
.frame-container {
  width: 100%;
  height: clamp(560px, 82vh, 660px);
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: #000;
}
.frame-img {
  width: 65%;
  height: 84%;
  object-fit: contain;
  transform: translateY(-2%) scale(1.3);
  z-index: 2;
  pointer-events: none;
  user-select: none;
  display: block;
}
.frame-slider {
  position: absolute;
  left: 50%;
  bottom: 9%;
  transform: translateX(-50%);
  width: 480px;
  z-index: 10;
}
.frame-slider::-webkit-slider-thumb {
  width: 29px;
  height: 29px;
  background: linear-gradient(135deg, #279EFF 40%, #36edb8 100%);
  border: 3px solid #fff;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0,80,150,0.16);
  cursor: pointer;
  transition: background 0.3s;
  appearance: none;
  margin-top: -11px;
}
.frame-slider::-moz-range-thumb {
  width: 29px;
  height: 29px;
  background: linear-gradient(135deg, #279EFF 40%, #36edb8 100%);
  border: 3px solid #fff;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0,80,150,0.16);
  cursor: pointer;
  transition: background 0.3s;
}
.frame-slider::-ms-thumb {
  width: 29px;
  height: 29px;
  background: linear-gradient(135deg, #279EFF 40%, #36edb8 100%);
  border: 3px solid #fff;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0,80,150,0.16);
  cursor: pointer;
  transition: background 0.3s;
}
.frame-slider {
  -webkit-appearance: none;
  background: #e4eef5;
  height: 6px;
  border-radius: 8px;
  outline: none;
}
.frame-slider::-webkit-slider-runnable-track {
  height: 6px;
  border-radius: 8px;
  background: #e4eef5;
}
.frame-slider::-moz-range-track {
  height: 6px;
  border-radius: 8px;
  background: #e4eef5;
}
.frame-slider::-ms-fill-lower,
.frame-slider::-ms-fill-upper {
  height: 6px;
  border-radius: 8px;
  background: #e4eef5;
}
.frame-slider::-ms-tooltip {
  display: none;
}
.ThinWall-title {
  position: absolute;
  top: 18%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  font-family: 'PingFang TC', 'Open Sans', 'Noto Sans TC', 'Heiti TC', sans-serif;
  font-size: clamp(1rem, 2vw, 2rem);
  text-align: center;
  line-height: 1.5;
  z-index: 10;
  max-width: 90vw;
  letter-spacing: 0.05em;
  padding: 0 0.1rem;
}
.line-1 {
  display: block;
  color: #FFF;
  font-size: 16px;
  font-weight: 350;
  line-height: 1.9;
  letter-spacing: 0.02em;
}
.gradient {
  background: linear-gradient(to bottom, #279EFF, #36edb8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
  font-weight: 900;
  letter-spacing: 0.16em;
}
.animate-line {
  opacity: 0;
  animation: fadeUp 0.8s ease-out forwards;
}
.delay-1 {
  animation-delay: 0.3s;
}
.delay-2 {
  animation-delay: 0.6s;
}
@media (max-width: 900px) {
  .ThinWall.is-english {
    display: flex;
    flex-direction: column;
    padding-top: 32px;
  }

  .ThinWall.is-english .ThinWall-title {
    position: relative;
    top: auto;
    left: auto;
    order: -1;
    width: calc(100% - 2rem);
    max-width: 760px;
    margin: 0 auto 12px;
    padding: 0;
    transform: none;
  }

  .ThinWall.is-english .ThinWall-title .gradient {
    letter-spacing: 0.08em;
  }

  .ThinWall.is-english .ThinWall-animation {
    width: 100%;
  }

  .ThinWall.is-english .frame-container {
    height: clamp(400px, calc(90vw + 40px), 780px);
  }

  .ThinWall.is-english .frame-img {
    transform: translateY(-76vw);
  }

  .frame-container {
    height: clamp(608px, calc(140vw + 48px), 1028px);
  }

  .frame-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: auto;
    object-fit: initial;
    transform: translateY(calc(-40vw + 48px));
  }

  .ThinWall-title {
    top: clamp(90px, 20vw, 160px);
  }

  .frame-slider {
    bottom: 24px;
    width: 70vw !important;
    min-width: 200px;
    max-width: 99vw;
    height: 8px;
  }

  .frame-slider::-webkit-slider-runnable-track {
    height: 8px;
  }

  .frame-slider::-moz-range-track {
    height: 8px;
  }

  .frame-slider::-ms-fill-lower,
  .frame-slider::-ms-fill-upper {
    height: 8px;
  }

  .frame-slider::-webkit-slider-thumb {
    width: 36px;
    height: 36px;
    margin-top: -14px;
  }

  .frame-slider::-moz-range-thumb {
    width: 36px;
    height: 36px;
  }

  .frame-slider::-ms-thumb {
    width: 36px;
    height: 36px;
  }
}
</style>
